'use client';

import { FC, useState } from 'react';
import { ClickableDiv } from '../../../../../packages/ui/components/button';
import { cn } from '../../../../../packages/ui/lib/lib';
import { IcoMoonIcon } from '../../../../../packages/ui/components/icons';
import { NoLeaderboardData } from '../leaderboard/no-leaderboard';
import { useTranslations } from 'next-intl';
import { useTriviaLeaderboard } from '@wwe-portal/web/providers/queries/trivia-rumble';
import {
  PlayerLeaderboard,
  PlayerLeaderboardSkeleton,
} from '../leaderboard/player-leaderboard';
import { TriviaLeaderboardSectionLayout } from './trivia-leaderboard-section-layout';
import { useGetCurrentUser } from '@wwe-portal/web/providers/queries/user-info';
import { useWindowSize } from '../../../../../packages/ui/hooks/use-window-size';

const triviaLeaderboardTabList = [
  {
    key: 'global',
    icon: 'fill',
    title: 'All_time',
  },
  {
    key: 'monthly',
    icon: 'skeleton',
    title: 'month',
  },
  {
    key: 'weekly',
    icon: 'line',
    title: 'week',
  },
] as const;

const Leaderboard: FC<{ activeId: 'global' | 'monthly' | 'weekly' }> = ({
  activeId,
}) => {
  const t = useTranslations();
  const { data, isLoading } = useTriviaLeaderboard();
  const currentUser = useGetCurrentUser();
  const { width } = useWindowSize();

  const currentLeaderboard = data?.data.data[activeId];
  if (!currentLeaderboard || isLoading)
    return <PlayerLeaderboardSkeleton compact={false} />;

  if (currentLeaderboard.length === 0)
    return (
      <NoLeaderboardData
        description={t('trivia_empty_leaderboard_description')}
        title={t('trivia_empty_leaderboard_title')}
      />
    );

  const compact = width >= 1620 && width < 1800;

  return (
    <PlayerLeaderboard
      compact={compact}
      rowClassName="odd:bg-midnightExpressShade2 md:even:bg-bgColor15 even:bg-midnightExpressShade3"
      containerClassnames="bg-transparent"
      mobileHeader={null}
      desktopHeader={null}
      classNames="py-2"
      data={currentLeaderboard.slice(0, 10).map((item) => ({
        rank: item.playerRank,
        name:
          currentUser?.userId === item.userId
            ? (currentUser?.temporaryNickname ?? currentUser?.nickname ?? '-')
            : item.playerName || '-',
        faction: item.factionName,
        userId: item.userId,
        points: item.playerPoints,
      }))}
      currentUserId={currentUser?.userId}
    />
  );
};

export const TriviaLeaderboardSection = () => {
  const [activeSelectItem, setActiveSelectItem] = useState<
    'global' | 'monthly' | 'weekly'
  >('global');

  const t = useTranslations();
  return (
    <TriviaLeaderboardSectionLayout
      tabs={
        <>
          {triviaLeaderboardTabList.map((item) => (
            <ClickableDiv
              key={item.key}
              onClick={() => {
                setActiveSelectItem(item.key);
              }}
              className={cn(
                'navigation-button bg-bgColor1 hover:text-textColor4 hover:bg-bgColor4 text-text400 flex h-[30px] w-full items-center justify-center rounded-[6px] px-[15px] text-xs transition duration-300 ease-linear md:text-sm',
                {
                  'text-textColor4 bg-bgColor4': activeSelectItem === item.key,
                }
              )}
            >
              <IcoMoonIcon
                iconName={`calendar-${item.icon}`}
                className="h-[14px] w-[14px]"
              />
              <span className="whitespace-nowrap pl-2 capitalize">
                {t(item.title)}
              </span>
            </ClickableDiv>
          ))}
        </>
      }
    >
      <Leaderboard activeId={activeSelectItem} />
    </TriviaLeaderboardSectionLayout>
  );
};
